import axios from 'axios';
import Cookies from 'js-cookie';
import i18next from 'i18next';
import { getCookieJSON } from 'utils/cookieUtils';
import { env } from 'config/env';

const baseURL = env.baseUrl;

let contextAccessToken: string | null = null;

const POST_LOGIN_401_GRACE_MS = 10_000;
let lastAuthAt = 0;

export const setApiAccessToken = (token: string | null): void => {
    contextAccessToken = token && token.length > 0 ? token : null;
};

export const markLastAuthAt = (): void => {
    lastAuthAt = Date.now();
};

const isWithinPostLoginGrace = (): boolean =>
    lastAuthAt > 0 && Date.now() - lastAuthAt < POST_LOGIN_401_GRACE_MS;

export const resolveAuthToken = (
    contextToken: string | null,
    cookieToken?: string
): string | undefined => {
    if (contextToken && contextToken.length > 0) {
        return contextToken;
    }
    if (cookieToken && cookieToken.length > 0) {
        return cookieToken;
    }
    return undefined;
};

const getPkAdminCookieToken = (): string | undefined => {
    const cookies = getCookieJSON<{ token?: string }>('pk-admin');
    return cookies?.token;
};

export const getApiAuthToken = (): string => {
    const token = resolveAuthToken(contextAccessToken, getPkAdminCookieToken());
    if (!token) {
        throw new Error('Missing auth token');
    }
    return token;
};

const allowedLanguages = new Set([
    'dansk',
    'english',
    'tysk',
    'svensk',
    'norsk',
]);

const getLanguageKey = (): string => {
    const raw = (Cookies.get('pk-language') ?? '').trim().toLowerCase();

    if (allowedLanguages.has(raw)) {
        return raw;
    }

    const i18nRaw = (i18next.language ?? '').trim().toLowerCase();
    if (allowedLanguages.has(i18nRaw)) {
        return i18nRaw;
    }

    return 'dansk';
};

export const apiBase = axios.create({
    baseURL,
    timeout: 40000,
    headers: {
        Accept: 'application/json',
    },
});

export const apiBasePublic = axios.create({
    baseURL,
    timeout: 40000,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
    },
});

export const ticketServiceApi = axios.create({
    baseURL: `https://pk-ticket-service-production.herokuapp.com/v2/api`,
    timeout: 4000,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
    },
});

export const authBase = axios.create({
    baseURL,
    timeout: 40000,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
});

authBase.interceptors.response.use(
    (response) => response,
    (error) => Promise.resolve(error.response)
);

authBase.interceptors.request.use(
    async (config) => {
        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = 'multipart/form-data';
        } else {
            config.headers['Content-Type'] = 'application/json';
        }
        return config;
    },
    (error) => Promise.reject(error)
);

const isSubscriptionSubscriber401 = (error: {
    response?: { status?: number; request?: { responseURL?: string } };
}): boolean =>
    !!(
        error?.response?.request?.responseURL &&
        error.response.request.responseURL.includes(
            '/v3/subscriptions/subscriber/'
        )
    );

const requestHadToken = (error: {
    config?: { headers?: { Token?: string } };
}): boolean => {
    const token = error?.config?.headers?.Token;
    return typeof token === 'string' && token.length > 0;
};

apiBase.interceptors.response.use(
    (response) => {
        if (response.status === 200) {
            const searchParams = new URLSearchParams(window.location.search);
            const someParam = searchParams.get('userSessionExpired');
            if (someParam === 'true') {
                searchParams.delete('userSessionExpired');
                let newUrl = `${window.location.pathname}`;
                if (searchParams.toString().length > 0) {
                    newUrl = `${window.location.pathname}?${searchParams}`;
                }
                window.history.pushState({ path: newUrl }, '', newUrl);
            }
        }
        return response;
    },
    (error) => {
        if (
            (error?.response?.status === 401 ||
                error?.response?.status === 403) &&
            !isSubscriptionSubscriber401(error) &&
            requestHadToken(error) &&
            !isWithinPostLoginGrace()
        ) {
            const searchParams = new URLSearchParams(window.location.search);
            const someParam = searchParams.get('userSessionExpired');
            if (!someParam) {
                searchParams.append('userSessionExpired', 'true');
                window.location.search = searchParams.toString();
            }
        }
        return Promise.resolve(error.response);
    }
);

apiBase.interceptors.request.use(
    (config) => {
        config.headers['Cache-Control'] = 'no-cache';
        config.headers.Pragma = 'no-cache';
        config.headers.Expires = '0';

        const token = resolveAuthToken(
            contextAccessToken,
            getPkAdminCookieToken()
        );
        if (!token) {
            return Promise.reject(new Error('Missing auth token'));
        }
        config.headers.Token = token;
        config.headers['Accept-Language'] = getLanguageKey();

        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = 'multipart/form-data';
        } else {
            config.headers['Content-Type'] = 'application/json';
        }
        return config;
    },
    (error) => Promise.reject(error)
);

apiBasePublic.interceptors.response.use(
    (response) => response,
    (error) => Promise.resolve(error.response)
);

apiBasePublic.interceptors.request.use((config) => {
    if (config.data instanceof FormData) {
        config.headers['Content-Type'] = 'multipart/form-data';
    } else {
        config.headers['Content-Type'] = 'application/json';
    }

    config.headers['Accept-Language'] = getLanguageKey();
    return config;
});
